import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const SECTIONS = [
  {
    title: '1. Introduction',
    body: [
      'Welcome to Physign Technologies Pvt. Ltd. ("Physign", "we", "our", or "us"). Physign is a healthcare platform that connects users with qualified doctors, nurses, physiotherapists, and other healthcare professionals for doorstep, online and clinic services.',
      'By accessing or using our platform (website/app), you agree to comply with and be bound by these Terms & Conditions.',
    ],
  },
  {
    title: '2. Services Offered',
    body: ['Physign provides:'],
    list: [
      'Doctor consultations (online & at-home)',
      'Nursing care services',
      'Physiotherapy services',
      'Doorstep doctor visits',
      'Healthcare assistance at doorstep (within 30 minutes where available)',
      'Clinic provider listings — for clinics, hospitals and service providers',
    ],
    footer: 'We act as a facilitator, not a direct healthcare provider.',
  },
  {
    title: '3. User Eligibility',
    list: [
      'You must be at least 18 years old to use our services.',
      'If booking for someone else, you confirm you have their consent.',
      'Anyone can book a service for themselves or a family member.',
      'All information provided must be accurate and complete.',
    ],
  },
  {
    title: '4. Registration & Account',
    list: [
      'Users may need to create an account.',
      'You are responsible for maintaining confidentiality of your login details.',
      'Any misuse of your account is your responsibility.',
    ],
  },
  {
    title: '5. Booking & Payments',
    list: [
      'All bookings are subject to availability.',
      'Prices may vary based on location, service and provider.',
      'Payments can be made via online modes or as specified.',
      'Cancellation and refund policies will apply as per service type.',
    ],
  },
  {
    title: '6. Cancellation & Refund Policy',
    list: [
      'Users can cancel bookings within the allowed time window.',
      'Refunds (if applicable) will be processed within 3–4 business days.',
      'Late cancellations may attract charges.',
    ],
  },
  {
    title: '7. Role of Physign',
    list: [
      'Physign is a technology platform, not a medical provider.',
      'We connect users with verified professionals.',
    ],
  },
  {
    title: '8. User Responsibilities',
    list: [
      'Users must provide accurate medical and personal information.',
      'Users are responsible for attending booked appointments on time.',
      'Users must maintain the confidentiality of their account credentials.',
      'Users shall not misuse healthcare services or provide false information.',
    ],
  },
  {
    title: '9. Healthcare Professional Responsibilities',
    list: [
      'Healthcare professionals must maintain valid licenses and certifications.',
      'Professionals are responsible for the medical advice, diagnosis, and treatment they provide.',
      'Professionals must comply with applicable healthcare laws and ethical standards.',
    ],
  },
  {
    title: '10. Emergency Services Disclaimer',
    body: [
      'Physign is not an emergency healthcare service. In case of a medical emergency, users should immediately contact local emergency services or visit the nearest hospital.',
    ],
  },
  {
    title: '11. Medical Records & Prescriptions',
    list: [
      'Users are responsible for reviewing prescriptions and medical advice provided by healthcare professionals.',
      'Physign is not responsible for errors in information provided by users or healthcare professionals.',
    ],
  },
  {
    title: '12. Teleconsultation Terms',
    list: [
      'Teleconsultations are subject to internet connectivity and technical availability.',
      'Physign does not guarantee uninterrupted audio/video communication.',
      'Consultations may be recorded only where legally permitted and with appropriate consent.',
    ],
  },
  {
    title: '13. Verification & Background Checks',
    list: [
      'Physign may verify healthcare professional credentials but does not guarantee medical outcomes.',
      'Users should independently evaluate healthcare decisions.',
    ],
  },
  {
    title: '14. Medical Disclaimer',
    body: ['All medical advice is provided by independent professionals. Physign is not responsible for:'],
    list: [
      'Misdiagnosis',
      'Treatment outcomes',
      'Medical negligence by service providers',
    ],
    footer: 'In case of emergency, please contact your local emergency services immediately.',
  },
  {
    title: '15. Service Availability',
    list: [
      'Services may not be available in all cities or locations.',
      'Physign reserves the right to add, modify, suspend, or discontinue services at any time.',
    ],
  },
  {
    title: '16. Fraud Prevention',
    list: [
      'Physign may suspend accounts suspected of fraudulent, abusive, or illegal activity.',
      'Multiple fake bookings or payment abuse may result in permanent account termination.',
    ],
  },
  {
    title: '17. Communications Consent',
    body: [
      'Users consent to receiving appointment reminders, service updates, OTPs, notifications, emails, SMS, and WhatsApp communications related to platform services.',
    ],
  },
  {
    title: '18. Force Majeure',
    body: [
      'Physign shall not be liable for service disruptions caused by events beyond reasonable control, including natural disasters, pandemics, government restrictions, internet outages, or technical failures.',
    ],
  },
  {
    title: '19. Provider Replacement',
    body: [
      'If a healthcare professional becomes unavailable, Physign may assign an alternative qualified provider, subject to user approval and availability.',
    ],
  },
  {
    title: '20. Modification of Services',
    body: [
      'Physign reserves the right to update platform features, pricing, policies, and service offerings without prior notice.',
    ],
  },
  {
    title: '21. Prohibited Activities',
    body: ['Users must not:'],
    list: [
      'Upload false medical records.',
      'Impersonate another person.',
      'Use the platform for unlawful purposes.',
      'Harass healthcare professionals or staff.',
      'Attempt unauthorized access to the platform.',
    ],
  },
  {
    title: '22. Data Privacy',
    list: [
      'User data is handled as per our Privacy Policy (physign.com/privacy-policy).',
      'We collect information only to improve services.',
      'We do not sell personal data to third parties.',
    ],
  },
  {
    title: '23. Intellectual Property',
    list: [
      'All content, branding and design belong to Physign.',
      'Unauthorized use is strictly prohibited.',
    ],
  },
  {
    title: '24. Limitation of Liability',
    body: ['Physign shall not be liable for:'],
    list: [
      'Any indirect or consequential damages',
      'Delays due to external factors',
      'Actions of third-party healthcare providers',
    ],
  },
  {
    title: '25. Termination',
    body: ['We may suspend or terminate access:'],
    list: [
      'If terms are violated',
      'For misuse or fraud',
      'Without prior notice in serious cases',
    ],
  },
  {
    title: '26. Changes to Terms',
    body: [
      'Physign reserves the right to update these Terms at any time. Users are advised to review them periodically.',
    ],
  },
  {
    title: '27. Governing Law',
    body: [
      'These Terms shall be governed by the laws of India. Jurisdiction: Gurugram, Haryana.',
    ],
  },
];

const Terms = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <SEO
        path="/terms"
        title="Terms & Conditions"
        description="Read the Terms & Conditions of Physign Technologies Pvt. Ltd. governing the use of our healthcare platform, services, bookings and provider listings."
      />

      {/* Header */}
      <header className="border-b border-slate-100 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-logo-link">
            <span className="text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Physign
            </span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/" className="text-slate-600 hover:text-[#3B1C73]">Home</Link>
            <Link to="/about" className="text-slate-600 hover:text-[#3B1C73]">About</Link>
            <Link to="/auth" className="bg-[#3B1C73] text-white px-4 py-2 rounded-full font-medium hover:bg-[#2c1457]">
              Login / Sign Up
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <div className="mb-10 pb-8 border-b border-slate-200">
          <span className="text-[#3B1C73] font-semibold uppercase tracking-wide text-sm">Legal</span>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
            Terms & Conditions
          </h1>
          <p className="text-sm text-slate-500 mt-3">Last updated: 06 June 2026</p>
          <p className="text-slate-600 mt-4 leading-relaxed">
            These Terms govern your use of Physign Technologies Pvt. Ltd. and its
            services. Please read them carefully before booking any appointment or
            using our platform.
          </p>
        </div>

        <div className="space-y-8">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-3">
                {section.title}
              </h2>
              {section.body && section.body.map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed mb-2">{p}</p>
              ))}
              {section.list && (
                <ul className="list-disc pl-6 space-y-1.5 text-slate-600">
                  {section.list.map((item, i) => (
                    <li key={i} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              )}
              {section.footer && (
                <p className="text-slate-600 leading-relaxed mt-3">{section.footer}</p>
              )}
            </section>
          ))}

          <section className="bg-purple-50 border border-purple-100 rounded-2xl p-6 mt-12">
            <h2 className="text-lg md:text-xl font-semibold text-[#3B1C73] mb-3">
              28. Contact Information
            </h2>
            <p className="text-slate-700 leading-relaxed">
              <strong>Physign Technologies Pvt. Ltd.</strong>
              <br />
              Wazirabad, Sector 52, Gurugram, Haryana
              <br />
              Email:{' '}
              <a href="mailto:info@physign.com" className="text-[#3B1C73] underline hover:no-underline">
                info@physign.com
              </a>
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-slate-100 py-6 mt-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <p>© 2026 Physign Technologies Pvt. Ltd. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/about" className="hover:text-[#3B1C73]">About</Link>
            <Link to="/terms" className="hover:text-[#3B1C73]">Terms</Link>
            <Link to="/privacy-policy" className="hover:text-[#3B1C73]">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
